import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { makeSseParser } from "@/lib/sse";
import ScoreBadge from "@/components/ScoreBadge";
import PanelCoach from "@/components/PanelCoach";
import PersonaBadge, { PERSONA_ICONS } from "@/components/PersonaBadge";

const PERSONA_ORDER = ["A1", "A2", "A3", "A4", "A5", "S1", "S2", "S3", "S4", "S5"];

function PersonaPending({ pid, meta }) {
  const Icon = PERSONA_ICONS[meta?.icon];
  const color = meta?.color || "#94a3b8";
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-5 relative overflow-hidden" data-testid={`persona-pending-${pid}`}>
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: color }} aria-hidden />
      <div className="flex items-center gap-2 mb-3 opacity-70">
        {Icon && <Icon size={14} strokeWidth={2.4} style={{ color }} />}
        <span className="mono-caps text-slate-500 text-[0.65rem]">{pid} · {meta?.name || "…"}</span>
      </div>
      <div className="mono-caps text-slate-400 text-[0.65rem] animate-pulse">Scoring…</div>
    </div>
  );
}

function TrafficLight({ passed }) {
  // Larger, glowing green/red dot with a subtle ring — reads at a glance.
  const color = passed ? "#059669" : "#DC2626";
  const glow = passed ? "rgba(5,150,105,0.35)" : "rgba(220,38,38,0.28)";
  return (
    <span
      className="inline-block w-4 h-4 rounded-full shrink-0"
      style={{
        backgroundColor: color,
        boxShadow: `0 0 0 3px ${glow}`,
      }}
      aria-label={passed ? "pass" : "open"}
    />
  );
}

function FlipBadge({ direction }) {
  const isPass = direction === "new_pass";
  const bg = isPass ? "bg-emerald-600" : "bg-rose-600";
  const label = isPass ? "NEW PASS" : "NEW OPEN";
  return (
    <span className={`inline-block ${bg} text-white mono-caps text-[0.55rem] px-1.5 py-0.5 rounded ml-2 align-middle`}>
      {label}
    </span>
  );
}

function PersonaCard({ p, meta, onAsk, flippedByItemIndex }) {
  const passed = !!p.overall_pass;
  const border = passed ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white";
  const badge = passed ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700";
  const color = meta?.color || "#334155";
  const Icon = PERSONA_ICONS[meta?.icon] || null;
  return (
    <div
      className={`rounded-xl border ${border} p-5 relative overflow-hidden`}
      data-testid={`persona-${p.id}`}
    >
      {/* Colored corner rail so each of the 10 personas reads distinct at a glance */}
      <span
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="mono-caps text-slate-500 mb-1 flex items-center gap-2">
            {Icon && <Icon size={12} strokeWidth={2.4} style={{ color }} />}
            <span style={{ color }}>{p.stage === "angel" ? "Angel" : "Seed"}</span>
            <span className="text-slate-400">· {p.check_size || ""}</span>
          </div>
          <div className="font-display text-xl leading-tight">
            <span className="text-slate-400 mr-2">{p.id}</span>{p.name}
          </div>
        </div>
        <span className={`mono-caps px-2.5 py-1 rounded-md text-[0.65rem] ${badge}`} data-testid={`persona-status-${p.id}`}>
          {passed ? "PASS" : `${p.passed_count ?? 0}/3`}
        </span>
      </div>
      {meta?.voice_preview && (
        <blockquote
          className="text-xs text-slate-600 italic leading-snug border-l-2 pl-3 mb-3"
          style={{ borderColor: color }}
          data-testid={`persona-voice-${p.id}`}
        >
          "{meta.voice_preview}"
        </blockquote>
      )}
      <ul className="space-y-2 mt-2">
        {(p.checklist || []).map((c, i) => {
          const flipDir = flippedByItemIndex?.[i];
          return (
            <li key={i} className="flex gap-3 items-start text-sm" data-testid={`persona-${p.id}-item-${i}`}>
              <TrafficLight passed={!!c.passed} />
              <div className="min-w-0 flex-1">
                <div className={`${c.passed ? "text-slate-800" : "text-slate-600"} leading-snug`}>
                  {c.item}
                  {flipDir && <FlipBadge direction={flipDir} />}
                </div>
                {c.evidence && (
                  <div className="text-xs text-slate-500 mt-1 italic leading-snug">{c.evidence}</div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex justify-end">
        <button
          className="mono-caps text-xs hover:opacity-80 underline underline-offset-4 decoration-slate-300 flex items-center gap-1"
          onClick={() => onAsk(p.id)}
          data-testid={`ask-${p.id}-btn`}
          style={{ color }}
        >
          Ask {p.id} →
        </button>
      </div>
    </div>
  );
}

export default function Verdict() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [runningPanel, setRunningPanel] = useState(false);
  const [panelError, setPanelError] = useState(null);
  const [coachPersonaId, setCoachPersonaId] = useState(null);
  const [personaMeta, setPersonaMeta] = useState({}); // { A1: {..color, icon}, ... }
  const [panelDiff, setPanelDiff] = useState({ flipped: [], totals: {} });
  const [livePanelPersonas, setLivePanelPersonas] = useState(null); // { A1: {...}, ... } while streaming
  const [livePanelActive, setLivePanelActive] = useState(false);

  useEffect(() => {
    (async () => {
      const [ideaR, personasR] = await Promise.all([
        api.get(`/ideas/${id}`),
        api.get(`/personas`),
      ]);
      setIdea(ideaR.data.idea);
      setPanelDiff(ideaR.data.panel_diff || { flipped: [], totals: {} });
      const map = {};
      for (const p of (personasR.data.personas || [])) map[p.id] = p;
      setPersonaMeta(map);
      // Deep-link support: /verdict?coach=A1 auto-opens that persona's coach drawer
      const coachId = searchParams.get("coach");
      if (coachId && map[coachId]) {
        setCoachPersonaId(coachId);
        // Remove the param from the URL so a back-forward doesn't re-open the drawer.
        searchParams.delete("coach");
        setSearchParams(searchParams, { replace: true });
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !idea) return <div className="p-14 text-slate-500">Loading…</div>;

  const panel = idea.panel_results;
  const panelHasError = panel && panel.error;
  // RING-TIER-03 · Verdict-leak hotfix. Docs unlock on tier2_complete
  // (Ring 4 + panel done). Never on the deprecated idea.verdict field.
  const canGenerate = idea.status === "complete" && (idea.tier_status || {}).tier2_complete;

  async function generate() {
    setGenerating(true);
    try {
      await api.post(`/ideas/${id}/generate-docs`);
      nav(`/ideas/${id}/documents`);
    } catch (e) {
      setGenerating(false);
    }
  }

  async function retryPanel() {
    setRunningPanel(true);
    setPanelError(null);
    setLivePanelPersonas({}); // start with empty (all pending)
    setLivePanelActive(true);
    const parse = makeSseParser();
    let finalIdea = null;
    let finalDiff = null;
    try {
      const resp = await fetch(`${API}/ideas/${id}/panel/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        let detail = "Panel run failed.";
        try {
          const j = await resp.json();
          detail = j?.detail || detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunkText = decoder.decode(value, { stream: true });
        for (const frame of parse(chunkText)) {
          if (frame.event === "persona") {
            const p = frame.data;
            setLivePanelPersonas((prev) => ({ ...(prev || {}), [p.id]: p }));
          } else if (frame.event === "done") {
            finalIdea = frame.data.idea;
            finalDiff = frame.data.panel_diff;
          } else if (frame.event === "error") {
            throw new Error(frame.data.detail || "Panel stream errored.");
          }
        }
      }
      if (finalIdea) {
        setIdea(finalIdea);
        setPanelDiff(finalDiff || { flipped: [], totals: {} });
      }
    } catch (e) {
      setPanelError(e?.message || "Panel run failed. Try again.");
    } finally {
      setRunningPanel(false);
      setLivePanelActive(false);
      // Small delay before clearing live view so the founder sees the final grid smoothly.
      setTimeout(() => setLivePanelPersonas(null), 800);
    }
  }

  // RING-TIER-03 · Verdict-leak hotfix. Frame color no longer keys off the
  // deprecated idea.verdict LLM-parsed field. Use the deterministic Tier
  // labels: tier2_complete → emerald frame; tier1_complete → amber; else
  // neutral. Never render a GO/CONDITIONAL/NO-GO-derived class.
  const tier = idea.tier_status || {};
  const verdictBorder = tier.tier2_complete
    ? "border-emerald-500 bg-emerald-50/40"
    : tier.tier1_complete
      ? "border-amber-500 bg-amber-50/40"
      : "border-slate-300 bg-slate-50/40";

  const personasById = {};
  if (panel && Array.isArray(panel.personas)) {
    for (const p of panel.personas) personasById[p.id] = p;
  }
  const orderedPersonas = PERSONA_ORDER.map((id2) => personasById[id2]).filter(Boolean);

  // Build a lookup of flipped items per persona so PersonaCard can badge them.
  const flippedLookup = {};
  for (const f of (panelDiff.flipped || [])) {
    if (!flippedLookup[f.persona_id]) flippedLookup[f.persona_id] = {};
    flippedLookup[f.persona_id][f.item_index] = f.direction;
  }
  const hasDiff = (panelDiff.flipped || []).length > 0;

  return (
    <div className="min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 py-14">
        <button
          className="mono-caps hover:text-slate-900 transition-colors mb-10"
          onClick={() => nav("/dashboard")}
          data-testid="verdict-back-to-dashboard"
        >
          ← Dashboard
        </button>

        <div className="mono-caps mb-4">Ring 3 · Full evaluation</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-2">{idea.title}</h1>

        <div className="text-slate-600 mt-6 mb-12">
          Gauntlet complete · Rings 1–4 scored ·{" "}
          <span className="font-semibold text-slate-900">
            R1 {idea.ring1_score?.score ?? "—"} / R2 {idea.ring2_score?.score ?? "—"} / R3 {idea.ring3_score?.score ?? "—"} / R4 {idea.ring4_score?.score ?? "—"}
          </span>
        </div>

        <div className="flex flex-wrap gap-3 mb-12">
          <ScoreBadge score={idea.ring1_score} />
          <ScoreBadge score={idea.ring2_score} />
          <ScoreBadge score={idea.ring3_score} />
          <ScoreBadge score={idea.ring4_score} />
        </div>

        {/* Ring 3 final evaluation — narrative content only. The GO /
            CONDITIONAL / NO-GO word emitted by the Ring 3 prompt is
            stripped before render (RING-TIER-03 verdict-leak hotfix).
            Biggest strength / risk / first move / open question survive. */}
        {idea.final_summary && (
          <div className={`border-l-4 pl-8 mb-12 py-4 pr-6 rounded-r-xl ${verdictBorder}`} data-testid="final-summary">
            <div className="mono-caps ring-accent-3 mb-4">Ring 3 · Full evaluation</div>
            <div className="text-slate-800 leading-relaxed whitespace-pre-wrap">
              {(idea.final_summary || "")
                .replace(/\[RING\s*3.*?\]/gis, "")
                // Strip the LLM-emitted verdict label — line-anchored so
                // we do not eat prose that legitimately contains the word.
                // Matches "Verdict: GO", "Verdict — NO-GO", "Verdict: CONDITIONAL"
                // (with optional bold/italic markdown) on its own line.
                .replace(/^\s*[*_]*\s*verdict\s*[*_]*\s*[:\-—]\s*[*_]*\s*(go|no[- ]?go|conditional)\s*\.?\s*[*_]*\s*$/gim, "")
                // And any inline "Verdict: NO-GO." fragment inside a paragraph.
                .replace(/verdict\s*[:\-—]\s*(go|no[- ]?go|conditional)\s*\.?/gi, "")
                .trim()}
            </div>
          </div>
        )}

        {/* Shark Panel section */}
        <div className="mb-14" data-testid="shark-panel-section">
          <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="mono-caps ring-accent-4 mb-2">Ring 4 · Shark Panel</div>
              <h2 className="font-display text-3xl">The 10-Investor Audit</h2>
            </div>
            {panel && !panelHasError && (
              <div className="mono-caps text-slate-500" data-testid="panel-totals">
                Angel {panel.totals?.angel_passed ?? 0}/5 · Seed {panel.totals?.seed_passed ?? 0}/5 · Total {panel.totals?.total_passed ?? 0}/10
                {panel.totals?.unicorn_sweep ? " · UNICORN SWEEP" : ""}
              </div>
            )}
          </div>

          {!panel && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-slate-600" data-testid="panel-not-run">
              The Shark Panel has not been run yet. Complete Ring 4 to trigger it, or run it manually below.
              <div className="mt-4">
                <button className="btn-primary" onClick={retryPanel} disabled={runningPanel} data-testid="run-panel-btn">
                  {runningPanel ? "Running panel…" : "Run the Shark Panel"}
                </button>
              </div>
            </div>
          )}

          {panelHasError && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-6" data-testid="panel-error">
              <div className="mono-caps text-red-700 mb-2">Panel run failed</div>
              <div className="text-sm text-red-800 mb-4">{panel.error}</div>
              {panelError && <div className="text-sm text-red-800 mb-4">{panelError}</div>}
              <button className="btn-primary" onClick={retryPanel} disabled={runningPanel} data-testid="retry-panel-btn">
                {runningPanel ? "Re-running…" : "Re-run the Shark Panel"}
              </button>
            </div>
          )}

          {panel && !panelHasError && (
            <>
              {livePanelPersonas && (
                <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-6 mb-8" data-testid="panel-live-stream">
                  <div className="mono-caps text-blue-800 mb-3">
                    Panel scoring · {Object.keys(livePanelPersonas).length}/10 landed
                    {livePanelActive && <span className="ml-2 animate-pulse">…</span>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {PERSONA_ORDER.map((pid) => {
                      const scored = livePanelPersonas[pid];
                      const meta = personaMeta[pid];
                      if (!scored) return <PersonaPending key={pid} pid={pid} meta={meta} />;
                      const Icon = PERSONA_ICONS[meta?.icon];
                      const color = meta?.color || "#334155";
                      const passed = scored.overall_pass;
                      return (
                        <div
                          key={pid}
                          className={`rounded-xl border p-4 relative overflow-hidden ${passed ? "border-emerald-400 bg-emerald-50/50" : "border-slate-200 bg-white"}`}
                          data-testid={`persona-live-${pid}`}
                        >
                          <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: color }} aria-hidden />
                          <div className="flex items-center gap-2 mb-2">
                            {Icon && <Icon size={14} strokeWidth={2.4} style={{ color }} />}
                            <span className="mono-caps text-[0.6rem]" style={{ color }}>{pid}</span>
                            <span className={`ml-auto mono-caps px-2 py-0.5 rounded text-[0.55rem] ${passed ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`}>
                              {passed ? "PASS" : `${scored.passed_count || 0}/3`}
                            </span>
                          </div>
                          <div className="text-xs font-semibold leading-tight truncate">{scored.name}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {hasDiff && !livePanelPersonas && (
                <div className="rounded-xl border border-blue-300 bg-blue-50/50 p-6 mb-8" data-testid="panel-diff-strip">
                  <div className="mono-caps text-blue-800 mb-2">Since your last panel run</div>
                  <div className="text-sm text-slate-800 leading-relaxed">
                    <span className="font-semibold text-emerald-700">+{panelDiff.totals?.new_pass || 0} passing</span>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="font-semibold text-rose-700">{panelDiff.totals?.new_open || 0} regressed</span>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="text-slate-600">Total: {panelDiff.totals?.prev_total ?? 0} → <span className="font-semibold text-slate-900">{panelDiff.totals?.curr_total ?? 0}</span> /10</span>
                  </div>
                </div>
              )}
              {panel.summary && (
                <div className="rounded-xl border border-slate-200 bg-white p-6 mb-8" data-testid="panel-summary">
                  <div className="mono-caps text-slate-500 mb-2">Panel summary</div>
                  <p className="text-slate-800 leading-relaxed">{panel.summary}</p>
                </div>
              )}

              {Array.isArray(panel.biggest_gaps) && panel.biggest_gaps.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50/50 p-6 mb-10" data-testid="panel-gaps">
                  <div className="mono-caps text-amber-800 mb-3">Biggest gaps to close</div>
                  <ul className="space-y-2">
                    {panel.biggest_gaps.map((g, i) => (
                      <li key={i} className="text-slate-800 text-sm leading-snug">— {g}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="mono-caps text-slate-500 mb-4">Angel personas · A1–A5</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                  {orderedPersonas.filter((p) => p.stage === "angel").map((p) => (
                    <PersonaCard
                      key={p.id}
                      p={p}
                      meta={personaMeta[p.id]}
                      flippedByItemIndex={flippedLookup[p.id]}
                      onAsk={setCoachPersonaId}
                    />
                  ))}
                </div>

                <div className="mono-caps text-slate-500 mb-4">Seed personas · S1–S5</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {orderedPersonas.filter((p) => p.stage === "seed").map((p) => (
                    <PersonaCard
                      key={p.id}
                      p={p}
                      meta={personaMeta[p.id]}
                      flippedByItemIndex={flippedLookup[p.id]}
                      onAsk={setCoachPersonaId}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button
                  className="btn-ghost"
                  onClick={retryPanel}
                  disabled={runningPanel}
                  data-testid="rerun-panel-btn"
                >
                  {runningPanel ? "Re-running…" : "Re-run the Shark Panel"}
                </button>
                {panelError && (
                  <span className="text-sm text-red-700" data-testid="panel-retry-error">{panelError}</span>
                )}
              </div>
            </>
          )}
        </div>

        {canGenerate ? (
          <div className="space-y-4">
            <p className="text-slate-600 max-w-2xl">
              Generate the five-document Gauntlet package: Simple Brief, Full-Depth Brief,
              Checklist Scorecard, Investor One-Pager, and Shark Panel Report.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                className="btn-commit"
                disabled={generating}
                onClick={generate}
                data-testid="verdict-generate-docs"
              >
                {generating ? "Generating…" : "Generate document package"}
              </button>
              <button
                className="btn-ghost"
                onClick={() => nav(`/ideas/${id}/plan`)}
                data-testid="verdict-open-plan"
              >
                Open plan vs. actual →
              </button>
            </div>
          </div>
        ) : (
          <div className="text-slate-500 max-w-lg" data-testid="no-docs-explainer">
            Tier 2 documents unlock once Ring 4 (Growth Architecture) and the Shark Panel run.
            Return to Ring 4 to tighten the plan and re-run the panel.
          </div>
        )}
      </div>
      {coachPersonaId && (
        <PanelCoach
          ideaId={id}
          personaId={coachPersonaId}
          onClose={() => setCoachPersonaId(null)}
        />
      )}
    </div>
  );
}
